<template>
  <div class="pt-[9px] sid-select">
    <div>
      <DividerStyle
        :config="config"
        :pressets="stylePressets"
      />

      <b-form-group
        label="Line Thickness"
      >
        <div class="flex items-center gap-[9px]">
          <b-input-group class="w-auto">
            <b-input
              v-model="width"
              :disabled="config.pressetID === 2 || config.pressetID === 1"
              :max="40"
              :min="0"
              class="!min-w-[60px] !h-[29px]"
              type="number"
            />

            <div class="form-control p-0 !min-w-[37px] !h-[29px] items-center justify-center d-flex pr-1">
              px
            </div>
          </b-input-group>
        </div>
      </b-form-group>

      <div
        v-click-outside="onClickOutsideTextColorPicker"
        class="position-relative"
      >
        <color-picker-flat
          v-if="showColorPicker"
          ref="colorMenuFlat"
          :color="lineColor"
          @input="applySolidColor"
        />
      </div>

      <b-form-group>
        <background-type
          v-if="canUseGradient"
          :value="colorTypeValue"
          :config="config"
          label="Color Type"
          @updated="onColorTypeUpdated"
        />

        <div
          v-if="!isGradientColor"
          class="flex justify-end"
        >
          <color-picker-placeholder
            ref="colorPickerInvoker"
            class="mr-2"
            :color="lineColor"
            @click.native="toggleColorPicker"
          />

          <span
            class="mt-2 block text-[#333333] underline text-[13px] cursor-pointer hover:opacity-80 line-height-[13px]"
            @click="resetLineColor"
          >
            Reset color
          </span>
        </div>

        <gradient-background
          v-else-if="canUseGradient"
          :config="config"
          direction-enabled
          @change="applyGradientColor"
        />
      </b-form-group>
    </div>

    <b-form-group label="Line Width">
      <vue-slider
        v-model="widthLine"
        :dot-size="17"
        :max="100"
        :min="1"
        class="editor-slider-1"
        tooltip="none"
      />
    </b-form-group>

    <b-form-group label="Alignment">
      <b-form-group>
        <b-btn-group>
          <b-btn
            variant="white"
            class="h-[29px] py-0 flex items-center justify-center"
            @click="align = 'start'"
          >
            <i
              :class="{'text-primary': align === 'start'}"
              class="fa fa-outdent"
            />
          </b-btn>
          <b-btn
            variant="white"
            class="h-[29px] py-0 flex  items-center justify-center"
            @click="align = 'center'"
          >
            <i
              :class="{'text-primary': align === 'center'}"
              class="fa fa-align-justify"
            />
          </b-btn>
          <b-btn
            variant="white"
            class="h-[29px] py-0 flex items-center justify-center"
            @click="align = 'end'"
          >
            <i
              :class="{'text-primary': align === 'end'}"
              class="fa fa-indent"
            />
          </b-btn>
        </b-btn-group>
      </b-form-group>
    </b-form-group>

    <template v-if="config.pressetID === 0">
      <b-form-group>
        <v-select
          v-model="style"
          :calculate-position="withPopper"
          :options="styles"
          :reduce="style => style.value"
          :searchable="false"
          append-to-body
          class="custom-select"
        />
      </b-form-group>

      <b-form-group>
        <b-checkbox v-model="config.pilled">
          Pilled
        </b-checkbox>
      </b-form-group>
    </template>

    <SidbearSettingsGroup
      class="border-top"
      title="Padding & Margins"
    >
      <sid-linked-group
        label="Padding"
        :sid="`.--${section.sid}-divider`"
        rule-name="padding"
        :min="0"
        :angles="[
          {
            text: '',
            value: 'top'
          },
          {
            text: '',
            value: 'right'
          },
          {
            text: '',
            value: 'bottom'
          },
          {
            text: '',
            value: 'left'
          }
        ]"
      />

      <sid-linked-group
        label="Margin"
        :sid="`.--${section.sid}-divider`"
        rule-name="margin"
        :min="-2000"
        :max="2000"
        :angles="[
          {
            text: '',
            value: 'top'
          },
          {
            text: '',
            value: 'right'
          },
          {
            text: '',
            value: 'bottom'
          },
          {
            text: '',
            value: 'left'
          }
        ]"
      />
    </SidbearSettingsGroup>
  </div>
</template>

<script>
import _ from 'lodash'
import ModMixin from '@builder/components/mixins/ModMixin'
import popperMixin from '../mixins/popperMixin'
import DividerStyle from '@/components/editor/controls/divider-mod/DividerStyle'
import SidbearSettingsGroup from '@/components/editor/components/SidbearSettingsGroup'
import SidLinkedGroup from '@/components/editor/components/sid-controls/SidLinkedGroup'
import GradientBackground from '@/components/editor/controls/GradientBackground.vue'
import ColorPickerFlat from '@/components/editor/utils/ColorPickerFlat.vue'
import ColorPickerPlaceholder from '@/components/editor/utils/ColorPickerPlaceholder.vue'
import BackgroundType from '@/components/editor/controls/BackgroundType.vue'

export default {
  name: 'HorizontalLineMod',
  components: {
    BackgroundType,
    ColorPickerPlaceholder,
    ColorPickerFlat, GradientBackground, SidLinkedGroup, SidbearSettingsGroup, DividerStyle},
  mixins: [ModMixin, popperMixin],

  data () {
    return {
      showColorPicker: false,
      defaultGradient: [
        {
          color: 'rgb(119,171,255)',
          position: 0
        },
        {
          color: 'rgb(0,60,152)',
          position: 100
        }
      ],
      styles: [
        {
          value: 'none',
          label: 'No Border'
        },
        {
          value: 'solid',
          label: 'Solid'
        },
        {
          value: 'dashed',
          label: 'Dashed'
        },
        {
          value: 'dotted',
          label: 'Dotted'
        },
        {
          value: 'double',
          label: 'Double'
        },
        {
          value: 'outset',
          label: 'Outset'
        }
      ],
      stylePressets: [
        {
          id: 0,
          text: 'Custom',
          onSelect: () => {
            this.style = 'solid'
            this.width = 2
          }
        },
        {
          id: 1,
          preview: require('@/assets/images/element-presset/divider-mod/divider-1.svg'),
          onSelect: () => {
            this.style = 'none'
            this.width = 25
            this.$set(this.config, 'backgroundType', null)
          }
        },
        {
          id: 2,
          preview: require('@/assets/images/element-presset/divider-mod/divider-2.svg'),
          onSelect: () => {
            this.style = 'none'
            this.config.color = '#333333ff'
            this.$set(this.config, 'backgroundImage', 'M0 10.2066L12.9709 0L25.9419 10.2066V19.1374H0V10.2066Z')
            this.$set(this.config, 'backgroundType', null)
          }
        },
        {
          id: 3,
          preview: require('@/assets/images/element-presset/divider-mod/divider-3.svg'),
          onSelect: () => {
            this.style = 'dotted'
            this.width = 2
          }
        }
      ]
    }
  },

  computed: {
    margin () {
      if (!this.config.margin) {
        this.$set(this.config, 'margin', {})
      }

      return this.config.margin
    },
    canUseGradient () {
      return this.config.pressetID === 0
    },
    isGradientColor () {
      return this.canUseGradient && this.config.backgroundType === 'gradient'
    },
    colorTypeValue () {
      return this.isGradientColor ? 'gradient' : null
    },
    lineColor: {
      get () {
        return this.config.color || '#AEB1B4'
      },
      set (val) {
        this.$set(this.config, 'color', val)
      }
    },
    widthLine: {
      get () {
        return _.get(this.config.widthLine, this.resolutioner, _.get(this.config.widthLine, 'lg', this.config.widthLine)) || 100
      },
      set (val) {
        this.$set(this.config, 'widthLine', {..._.set(this.config.widthLine, [this.resolutioner], val)})
      }
    },
    style: {
      get () {
        return this.config.style || 'solid'
      },
      set (val) {
        this.$set(this.config, 'style', val)
      }
    },
    align: {
      get () {
        return _.get(this.config.align, this.resolutioner, _.get(this.config, 'align', 'center'))
      },
      set (val) {
        const align = typeof this.config.align === 'object'
          ? this.config.align
          : {
            lg: this.config.align || 'center',
            md: this.config.align || 'center',
            sm: this.config.align || 'center'
          }

        this.$set(this.config, 'align', {
          ...align,
          [this.resolutioner]: val
        })
      }
    },
    width: {
      get () {
        return parseInt(_.get(this.config.width, this.resolutioner, _.get(this.config.width, 'lg', this.config.width)))
      },
      set (val) {
        this.$set(this.config, 'width', {..._.set(typeof this.config.width === 'object' ? this.config.width : {}, [this.resolutioner], val)})
      }
    }
  },
  watch: {
    'config.pressetID' (val) {
      this.showColorPicker = false

      if (val !== 0 && this.config.backgroundType === 'gradient') {
        this.$set(this.config, 'backgroundType', null)
      }
    }
  },
  methods: {
    toggleColorPicker () {
      this.showColorPicker = !this.showColorPicker
    },
    ensureGradientConfig () {
      if (!Array.isArray(this.config.backgroundGradient) || !this.config.backgroundGradient.length) {
        this.$set(this.config, 'backgroundGradient', this.defaultGradient.map(item => ({...item})))
      }

      if (typeof this.config.gradientDirection === 'undefined' || this.config.gradientDirection === null) {
        this.$set(this.config, 'gradientDirection', 90)
      }
    },
    onColorTypeUpdated (val) {
      this.showColorPicker = false
      if (!this.canUseGradient) {
        this.$set(this.config, 'backgroundType', null)
        return
      }

      const isGradient = !!(val && val.value === 'gradient')

      if (isGradient) {
        this.$set(this.config, 'backgroundType', 'gradient')
        this.ensureGradientConfig()
        this.applyGradientColor(this.config)
        return
      }

      this.$set(this.config, 'backgroundType', null)
    },
    applySolidColor (event) {
      this.lineColor = event.hex8 || event.hex
      this.$set(this.config, 'backgroundType', null)
    },
    applyGradientColor (gradientConfig = this.config) {
      const gradient = Array.isArray(gradientConfig.backgroundGradient)
        ? gradientConfig.backgroundGradient
        : []

      if (gradient[0] && gradient[0].color) {
        this.lineColor = gradient[0].color
      }

      if (typeof gradientConfig.gradientDirection !== 'undefined') {
        this.$set(this.config, 'gradientDirection', gradientConfig.gradientDirection)
      }
    },
    resetLineColor () {
      this.lineColor = '#AEB1B4'
      this.showColorPicker = false
    },
    onClickOutsideTextColorPicker (event) {
      // Only act when the picker is open
      if (!this.showColorPicker) return

      // Ignore clicks on the invoker button/placeholder (so it can toggle normally)
      const invokerRef = this.$refs.colorPickerInvoker
      const invokerEl = invokerRef && (invokerRef.$el || invokerRef)
      if (invokerEl && typeof invokerEl.contains === 'function' && invokerEl.contains(event.target)) {
        return
      }

      this.showColorPicker = false
    }
  }
}
</script>
